import React, { useState, useEffect } from "react";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { Box, Flex, Text } from "rebass";
import {
  Card,
  TextContainer,
  TextStyle,
  Collapsible,
  ResourceList,
  EmptyState,
  Link,
  ChoiceList,
  Filters,
  Pagination,
  ResourceItem,
  Subheading,
  Icon,
  Tabs,
  Button,
} from "@shopify/polaris";
import { parseISO, format } from "date-fns";
import { MiniIcon } from "components/checks/new";
import {
  PhoneMajor,
  EmailMajor,
  CircleCancelMajor,
} from "@shopify/polaris-icons";
import useDebounce from "hooks/useDebounce";
import { searchCustomers } from "services/wpApi";
import { useFormikContext } from "formik";
import { useToast } from "providers/ToastProvider";

export default function CustomerSelection({
  setOpenSearchOrders,
  openSearchOrders,
  setStep,
}) {
  const setToast = useToast();
  const { values, setFieldValue, handleReset } = useFormikContext();

  const [customerNameQuery, setCustomerNameQuery] = useState("");
  const debouncedCustomerNameQuery = useDebounce(customerNameQuery, 500);

  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);

  const [recentCustomers, setRecentCustomers] = useState([]);

  // search recent shopify orders
  useEffect(() => {
    const query = debouncedCustomerNameQuery;

    searchCustomers({
      page,
      limit: 5,
      orderby: "ID",
      order: "DESC",
      search: query,
    })
      .then(({ data: { customers, max_num_pages, total } }) => {
        setRecentCustomers(customers);
        setTotalPages(max_num_pages);
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
        setToastMessage(
          "Unable to search your customers. Please contact support."
        );
      });
  }, [debouncedCustomerNameQuery, page]);
  return (
    <>
      <ResourceList
        items={recentCustomers}
        totalItemsCount={recentCustomers?.length || 0}
        filterControl={
          <Filters
            filters={
              [
                //   {
                //     key: "orderRiskLevel",
                //     label: "Risk Level",
                //     filter: (
                //       <ChoiceList
                //         title="Risk Level"
                //         titleHidden
                //         choices={[
                //           { label: "Medium", value: "MEDIUM" },
                //           { label: "High", value: "HIGH" },
                //         ]}
                //         selected={orderRiskLevels || []}
                //         onChange={(levels) => setOrderRiskLevels(levels)}
                //         allowMultiple
                //       />
                //     ),
                //     shortcut: true,
                //     hideClearButton: true,
                //     clearButton: false,
                //   },
                // {
                //   key: "orderStatus",
                //   label: "Status",
                //   filter: (
                //     <ChoiceList
                //       title="Fulfillment Status"
                //       titleHidden
                //       choices={[
                //         { label: "Medium", value: "MEDIUM" },
                //         { label: "High", value: "HIGH" },
                //       ]}
                //       selected={orderRiskLevels || []}
                //       onChange={(levels) => setOrderRiskLevels(levels)}
                //       allowMultiple
                //     />
                //   ),
                //   shortcut: true,
                //   hideClearButton: true,
                //   clearButton: false,
                // },
              ]
            }
            appliedFilters={[]}
            onClearAll={() => {}}
            queryValue={customerNameQuery}
            onQueryChange={setCustomerNameQuery}
            onQueryClear={() => {
              setCustomerNameQuery("");
            }}
            totalItemsCount={recentCustomers.length}
          />
        }
        resourceName={{
          singular: "customer",
          plural: "customers",
        }}
        emptyState={
          <EmptyState
            heading="No customers found"
            secondaryAction={{
              content: "Skip customer selection",
              onClick: () => {
                // TODO: empty state should just go to the create from scratch by default?
                handleReset();
                setStep(2);
              },
            }}
          >
            No new customer accounts found
            {customerNameQuery ? ` with "${customerNameQuery}"` : ""}.
          </EmptyState>
        }
        renderItem={(customer) => (
          <ResourceItem
            key={customer.id}
            onClick={() => {
              if (values.wcCustomerId && values.wcCustomerId == customer.id) {
                handleReset();
                setToastMessage(`Reset ID check customer & order details.`);
                return;
              }

              const phone = customer.phone;

              setFieldValue("firstName", customer?.first_name);
              setFieldValue("lastName", customer?.last_name);
              if (customer?.email) setFieldValue("email", customer.email);
              if (values.phone) setFieldValue("phone", phone);
              let deliveryMethods = [];
              if (customer?.email) deliveryMethods.push("email");
              if (values.phone) deliveryMethods.push("sms");
              setFieldValue("deliveryMethods", deliveryMethods);
              setFieldValue("wcCustomerId", customer.id);
              setToast(`Using ${customer.email}'s details for the ID check.`);
              setStep(3);
            }}
          >
            <div>
              {values.wcCustomerId && values.wcCustomerId == customer.id && (
                <Flex width={1} justifyContent="space-between">
                  <Subheading>SELECTED ORDER FOR APPLICANT DETAILS</Subheading>
                  <MiniIcon>
                    <Icon
                      color="critical"
                      source={CircleCancelMajor}
                      onClick={() => {
                        resetSelectedOrder();
                      }}
                    />
                  </MiniIcon>
                </Flex>
              )}
              <div className="ri-flex ri-flex-col ri-my-3 md:ri-my-0 md:ri-flex-row ri-justify-between">
                <div className="ri-my-2 md:ri-my-0">
                  <div className="ri-font-semibold">
                    {customer.first_name} {customer.last_name}
                  </div>
                  {customer?.email && <p>{customer.email}</p>}
                </div>
                <div className="ri-my-2 md:ri-my-0">
                  {customer?.phone && <div>{customer?.phone}</div>}
                </div>
                <div className="ri-my-2 md:ri-my-0">
                  {/* <div className="ri-font-semibold">Order {order.name}</div> */}
                  {customer?.created_at && (
                    <p>
                      {format(parseISO(customer.createdAt), "EEE LLL do y")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </ResourceItem>
        )}
      />
      {totalPages > 1 && (
        <>
          <Box my={2}>
            {page} / {totalPages}
          </Box>
          <Pagination
            hasPrevious={page - 1 >= 1}
            onPrevious={() => {
              setPage(page - 1);
            }}
            hasNext={page + 1 <= totalPages}
            onNext={() => {
              setPage(page + 1);
            }}
          />
        </>
      )}
    </>
  );
}
